import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { 
  HomeIcon, BarChart2Icon, FileTextIcon, ClipboardListIcon, 
  FileIcon, StoreIcon, TagIcon, MapPinIcon, PackageIcon, 
  BookOpenIcon, FileBarChartIcon, Settings2Icon, HelpCircleIcon,
  ChevronLeftIcon, ChevronRightIcon, UsersIcon, CalendarIcon,
  MailIcon, MessageSquareIcon, TruckIcon, UtensilsCrossedIcon,
  MenuIcon, XIcon
} from 'lucide-react';
import LogoImage from '../GastroFlow Logo Schwarz.png';

const menuItems = [
  {
    title: "Übersicht",
    items: [
      { id: 'dashboard', label: 'Dashboard', icon: HomeIcon, path: '/', permission: 'read:dashboard' },
      { id: 'analytics', label: 'Analytics', icon: BarChart2Icon, path: '/analytics', permission: 'read:analytics' },
    ]
  },
  {
    title: "Bestellungen & Finanzen",
    items: [
      { id: 'orders', label: 'Bestellungen', icon: FileTextIcon, path: '/page1', permission: 'manage:orders' },
      { id: 'billing', label: 'Abrechnungen', icon: FileIcon, path: '/BillingManagement', permission: 'manage:billing' },
    ]
  },
  {
    title: "Partner & Standorte",
    items: [
      { id: 'servicePartner', label: 'Flotten Partner', icon: StoreIcon, path: '/fleetpartner', permission: 'manage:partners' },
      { id: 'locations', label: 'Standorte', icon: MapPinIcon, path: '/locations', permission: 'manage:locations' },
    ]
  },
  {
    title: "Inventar & Rezepte",
    items: [
      { id: 'assets', label: 'Assets', icon: PackageIcon, path: '/assets', permission: 'manage:assets' },
      { id: 'recipes', label: 'Rezepte', icon: UtensilsCrossedIcon, path: '/recepies', permission: 'manage:recipes' },
    ]
  },
  {
    title: "Lieferungen",
    items: [
      { id: 'deliveries', label: 'Lieferungen', icon: TruckIcon, path: '/deliveries', permission: 'manage:deliveries' },
      { id: 'routes', label: 'Routen', icon: MapPinIcon, path: '/routes', permission: 'manage:routes' },
    ]
  },
  {
    title: "Personal",
    items: [
      { id: 'applicants', label: 'Fahrer', icon: UsersIcon, path: '/applicants', permission: 'manage:applicants' },
    ]
  },
  {
    title: "Dokumente & Kommunikation",
    items: [
      { id: 'documents', label: 'Dokumente', icon: FileBarChartIcon, path: '/documents', permission: 'read:documents' },
      { id: 'calendar', label: 'Kalender', icon: CalendarIcon, path: '/calendar', permission: 'read:calendar' },
      { id: 'email', label: 'E-Mail', icon: MailIcon, path: '/email', permission: 'manage:communication' },
      { id: 'chat', label: 'Chat', icon: MessageSquareIcon, path: '/chat', permission: 'manage:communication' },
    ]
  },
];

const Sidebar = ({ onResize }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [userPermissions, setUserPermissions] = useState([]);
  const sidebarWidth = isMobile ? (isOpen ? '100%' : '0') : (isOpen ? 240 : 70);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isLoading, isAuthenticated, getAccessTokenSilently } = useAuth0();

  // Check screen size and set mobile state
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  // Set default sidebar state based on device
  useEffect(() => {
    setIsOpen(!isMobile);
  }, [isMobile]);

  useEffect(() => {
    const checkToken = async () => {
      try {
        const token = await getAccessTokenSilently();
        console.log('Raw token:', token);
            
        if (token) {
          const parts = token.split('.');
          console.log('Token parts:', parts.length);
                
          if (parts.length === 3) {
            const decodedToken = JSON.parse(atob(parts[1]));
            console.log('Decoded token:', decodedToken);
            console.log('Audience:', decodedToken.aud);
            
            const permissions = decodedToken.permissions || 
                              decodedToken?.['https://api.gastroflow.com/auth/permissions'] ||
                              [];
            
            console.log('Found permissions:', permissions);
            setUserPermissions(permissions);
          }
        }
      } catch (error) {
        console.error('Error checking token:', error);
      }
    };

    if (isAuthenticated) {
      checkToken();
    }
  }, [isAuthenticated, getAccessTokenSilently]);

  useEffect(() => {
    if (!isMobile) {
      onResize(sidebarWidth);
    }
  }, [isOpen, onResize, sidebarWidth, isMobile]);

  const toggleSidebar = () => {
    setIsOpen(!isOpen);
  };

  const hasPermission = (requiredPermission) => {
    if (!requiredPermission) return true;
    
    console.log('Checking permission:', {
      required: requiredPermission,
      userHas: userPermissions,
      result: userPermissions.includes(requiredPermission)
    });
    
    return userPermissions.includes(requiredPermission);
  };

  const handleItemClick = (path) => {
    navigate(path);
    if (isMobile) {
      setIsOpen(false);
    }
  };

  if (isLoading) {
    return (
      <div className="fixed top-0 left-0 h-16 w-full flex items-center justify-center bg-white z-50 border-b border-gray-200 md:h-screen md:w-16 md:border-r md:border-b-0">
        <div className="text-center">Lädt...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="fixed top-0 left-0 h-16 w-full flex items-center justify-center bg-white z-50 border-b border-gray-200 md:h-screen md:w-16 md:border-r md:border-b-0">
        <div className="text-center">Nicht eingeloggt</div>
      </div>
    );
  }

  // Mobile Navigation Bar (shown when sidebar is closed on mobile)
  if (isMobile && !isOpen) {
    return (
      <>
        <div className="fixed top-0 left-0 w-full h-16 bg-white border-b border-gray-200 flex items-center justify-between px-4 z-50">
          <img 
            src={LogoImage}
            alt="Logo" 
            className="h-6" 
          />
          <button onClick={toggleSidebar} className="p-2 rounded-md hover:bg-gray-100">
            <MenuIcon size={24} />
          </button>
        </div>
        <div className="h-16"></div> {/* Spacer to prevent content overlap */}
      </>
    );
  }

  // Mobile Sidebar (full screen overlay)
  if (isMobile) {
    return (
      <motion.div
        className="fixed inset-0 bg-white z-50 flex flex-col overflow-hidden"
        initial={{ x: '-100%' }}
        animate={{ x: 0 }}
        exit={{ x: '-100%' }}
        transition={{ duration: 0.3, ease: 'easeInOut' }}
      >
        <div className="flex justify-between items-center py-4 px-4 border-b border-gray-200">
          <img 
            src={LogoImage}
            alt="Logo" 
            className="h-6" 
          />
          <button onClick={toggleSidebar} className="p-2 rounded-md hover:bg-gray-100">
            <XIcon size={24} />
          </button>
        </div>
        
        <nav className="flex-grow overflow-y-auto px-4 py-2">
          {menuItems.map((section, index) => {
            const hasAccessibleItems = section.items.some(item => hasPermission(item.permission));
            
            return hasAccessibleItems && (
              <div key={index} className="mb-5">
                <h2 className="px-2 py-1 text-sm font-medium text-gray-500 uppercase tracking-wider">
                  {section.title}
                </h2>
                <ul className="space-y-2">
                  {section.items.map((item) => (
                    hasPermission(item.permission) && (
                      <li key={item.id}>
                        <button
                          onClick={() => handleItemClick(item.path)}
                          className={`flex items-center w-full px-3 py-3 rounded-md transition-colors duration-200 ${
                            location.pathname === item.path 
                              ? 'text-black font-medium bg-gray-50'
                              : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                          }`}
                        >
                          <item.icon 
                            size={22} 
                            className={`mr-3 ${
                              location.pathname === item.path ? 'text-black' : 'text-gray-400'
                            }`} 
                            strokeWidth={location.pathname === item.path ? 2 : 1.5}
                          />
                          <span className="text-base">
                            {item.label}
                          </span>
                        </button>
                      </li>
                    )
                  ))}
                </ul>
              </div>
            );
          })}
        </nav>

        <div className="mt-auto p-4 border-t border-gray-100">
          <div className="flex justify-around">
            {hasPermission('manage:settings') && (
              <button className="p-3 rounded-full hover:bg-gray-100 text-gray-500 hover:text-gray-900" title="Einstellungen">
                <Settings2Icon size={24} />
              </button>
            )}
            {hasPermission('manage:help') && (
            <a href="https://intercom.help/gastroflow/de" target="_blank" rel="noopener noreferrer">
              <button className="p-3 rounded-full hover:bg-gray-100 text-gray-500 hover:text-gray-900" title="Hilfe">
                <HelpCircleIcon size={24} />
              </button>
            </a>)}
          </div>
        </div>
      </motion.div>
    );
  }

  // Desktop Sidebar
  return (
    <motion.div
      className="fixed top-0 left-0 h-screen bg-white text-black flex flex-col border-r border-gray-100 shadow-sm z-40"
      style={{ fontSize: '0.875rem' }}
      initial={{ width: sidebarWidth }}
      animate={{ width: sidebarWidth }}
      transition={{ duration: 0.3, ease: 'easeInOut' }}
    >
      <div className="flex justify-between items-center py-4 px-3 border-b border-gray-200">
        {isOpen ? (
          <div className="flex-grow flex justify-center">
            <img 
              src={LogoImage}
              alt="Logo" 
              className="h-5 w-auto" 
            />
          </div>
        ) : (
          <div className="flex-grow flex justify-center">
            <img 
              src={LogoImage}
              alt="Logo" 
              className="h-5 w-auto max-w-full" 
              style={{ objectFit: 'contain' }}
            />
          </div>
        )}
        <button onClick={toggleSidebar} className="flex items-center justify-center w-6 h-6 rounded-full hover:bg-gray-100">
          {isOpen ? <ChevronLeftIcon size={16} /> : <ChevronRightIcon size={16} />}
        </button>
      </div>
      
      <nav className="flex-grow overflow-y-auto px-3 py-2">
        {menuItems.map((section, index) => {
          const hasAccessibleItems = section.items.some(item => hasPermission(item.permission));
          
          return hasAccessibleItems && (
            <div key={index} className="mb-4">
              {isOpen && (
                <h2 className="px-3 py-1 text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {section.title}
                </h2>
              )}
              <ul className="space-y-1">
                {section.items.map((item) => (
                  hasPermission(item.permission) && (
                    <li key={item.id}>
                      <button
                        onClick={() => handleItemClick(item.path)}
                        className={`flex items-center w-full px-3 py-2 rounded-md transition-colors duration-200 ${
                          location.pathname === item.path 
                            ? 'text-black font-medium bg-gray-50'
                            : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                        }`}
                      >
                        <item.icon 
                          size={20} 
                          className={`${isOpen ? "mr-3" : "mx-auto"} ${
                            location.pathname === item.path ? 'text-black' : 'text-gray-400'
                          }`} 
                          strokeWidth={location.pathname === item.path ? 2 : 1.5}
                        />
                        <AnimatePresence>
                          {isOpen && (
                            <motion.span
                              initial={{ opacity: 0, width: 0 }}
                              animate={{ opacity: 1, width: 'auto' }}
                              exit={{ opacity: 0, width: 0 }}
                              transition={{ duration: 0.2 }}
                              className="whitespace-nowrap"
                            >
                              {item.label}
                            </motion.span>
                          )}
                        </AnimatePresence>
                      </button>
                    </li>
                  )
                ))}
              </ul>
            </div>
          );
        })}
      </nav>

      <div className="mt-auto p-4 border-t border-gray-100">
        <div className={`flex ${isOpen ? 'flex-row justify-between' : 'flex-col items-center space-y-4'}`}>
          {hasPermission('manage:settings') && (
            <button className="p-2 rounded-full hover:bg-gray-100 text-gray-500 hover:text-gray-900" title="Einstellungen">
              <Settings2Icon size={20} />
            </button>
          )}
          {hasPermission('manage:help') && (
          <a href="https://intercom.help/gastroflow/de" target="_blank" rel="noopener noreferrer">
            <button className="p-2 rounded-full hover:bg-gray-100 text-gray-500 hover:text-gray-900" title="Hilfe">
              <HelpCircleIcon size={20} />
            </button>
          </a>)}
        </div>
      </div>
    </motion.div>
  );
};

export default Sidebar;